import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { IconCalendar, IconChevronLeft, IconChevronRight, IconClock, IconClose } from "../icons";

/**
 * MARCAR A ENTREVISTA — o popup que aparece quando o candidato da Triagem de
 * Currículo passa para a situação "Entrevista".
 *
 * O RH clica no dia no calendário e no horário na grade; ao confirmar, o
 * candidato já entra na aba Entrevistas com esse dia e essa hora (ver
 * server/triagem.ts → agendarEntrevista). O objetivo é justamente esse: ninguém
 * redigita a data em outra tela, e o horário sai combinado, não "a combinar".
 *
 * Detalhes que importam:
 *  - as datas são montadas com ano/mês/dia LOCAIS (nunca `toISOString()`, que
 *    converte para UTC e joga o dia para trás em quem está em GMT-3);
 *  - o horário é texto HH:MM — é a hora combinada, não um instante no tempo;
 *  - dia no passado não é bloqueado (às vezes o RH lança uma entrevista que já
 *    aconteceu), mas a tela avisa em vez de deixar passar em silêncio.
 */

export interface Agendamento {
  /** YYYY-MM-DD */
  data: string;
  /** HH:MM (pode ficar vazio: a data é o obrigatório) */
  horario: string;
  etapa: string;
  entrevistador: string;
  observacao: string;
}

const ETAPAS = ["Triagem", "Técnica", "RH", "Gestor", "Final"];
const DIAS_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MESES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

/** Horários sugeridos: das 7h às 18h, de meia em meia hora. */
const HORARIOS: string[] = [];
for (let h = 7; h <= 18; h++) {
  HORARIOS.push(`${String(h).padStart(2, "0")}:00`);
  if (h < 18) HORARIOS.push(`${String(h).padStart(2, "0")}:30`);
}

/** Data → "YYYY-MM-DD" pelo calendário LOCAL (ver comentário do cabeçalho). */
function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** "2026-08-20" → "quinta-feira, 20 de agosto de 2026". */
function porExtenso(data: string): string {
  const [a, m, d] = data.split("-").map(Number);
  const dt = new Date(a, m - 1, d);
  if (Number.isNaN(dt.getTime())) return data;
  return dt.toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export default function AgendarEntrevistaModal({
  candidato,
  vaga,
  cargo,
  inicial,
  salvando = false,
  onCancelar,
  onConfirmar,
}: {
  candidato: string;
  vaga?: string;
  /** Função da vaga — vira o cargo da entrevista. */
  cargo?: string | null;
  /** Entrevista já marcada (reagendar) — preenche os campos. */
  inicial?: Partial<Agendamento> | null;
  salvando?: boolean;
  onCancelar: () => void;
  onConfirmar: (a: Agendamento) => void | Promise<void>;
}) {
  const hojeIso = iso(new Date());
  const [data, setData] = useState(inicial?.data ?? "");
  const [horario, setHorario] = useState(inicial?.horario ?? "");
  const [etapa, setEtapa] = useState(inicial?.etapa || "RH");
  // Quem vai conduzir: por padrão quem está logado (a mesma sessão que o resto
  // do sistema usa — ver App.tsx).
  const [entrevistador, setEntrevistador] = useState(
    inicial?.entrevistador || localStorage.getItem("sercamp-user") || "",
  );
  const [observacao, setObservacao] = useState(inicial?.observacao ?? "");
  const [erro, setErro] = useState<string | null>(null);

  // Mês exibido: o da entrevista já marcada, ou o mês corrente.
  const base = (inicial?.data ?? hojeIso).split("-").map(Number);
  const [mes, setMes] = useState(() => new Date(base[0], base[1] - 1, 1));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancelar();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancelar]);

  /** Células do mês: os vazios da primeira semana + os dias. */
  const celulas = useMemo(() => {
    const ano = mes.getFullYear();
    const m = mes.getMonth();
    const vazios = new Date(ano, m, 1).getDay(); // 0 = domingo
    const total = new Date(ano, m + 1, 0).getDate();
    const lista: (string | null)[] = Array(vazios).fill(null);
    for (let d = 1; d <= total; d++) lista.push(iso(new Date(ano, m, d)));
    return lista;
  }, [mes]);

  const passado = data !== "" && data < hojeIso;

  function confirmar() {
    if (!data) {
      setErro("Escolha o dia da entrevista no calendário.");
      return;
    }
    onConfirmar({
      data,
      horario: horario.trim(),
      etapa,
      entrevistador: entrevistador.trim(),
      observacao: observacao.trim(),
    });
  }

  return createPortal(
    // Este popup abre POR CIMA do modal da triagem: sem o stopPropagation, o
    // clique aqui subiria até o overlay de baixo e fecharia a triagem inteira.
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        e.stopPropagation();
        onCancelar();
      }}
    >
      <div className="modal-card agenda-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>
              <IconCalendar size={18} /> Marcar entrevista
            </h3>
            <p className="modal-sub">
              {candidato}
              {vaga ? ` · vaga ${vaga}` : ""}
              {cargo ? ` · ${cargo}` : ""}
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onCancelar}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body agenda-body">
          <div className="agenda-colunas">
            {/* ---------------- Calendário ---------------- */}
            <div className="agenda-calendario">
              <div className="agenda-mes">
                <button
                  type="button"
                  className="icon-btn"
                  title="Mês anterior"
                  onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() - 1, 1))}
                >
                  <IconChevronLeft size={17} />
                </button>
                <strong>
                  {MESES[mes.getMonth()]} de {mes.getFullYear()}
                </strong>
                <button
                  type="button"
                  className="icon-btn"
                  title="Próximo mês"
                  onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() + 1, 1))}
                >
                  <IconChevronRight size={17} />
                </button>
              </div>

              <div className="agenda-semana">
                {DIAS_SEMANA.map((d) => (
                  <span key={d}>{d}</span>
                ))}
              </div>

              <div className="agenda-dias">
                {celulas.map((dia, i) =>
                  dia === null ? (
                    <span key={`v${i}`} className="agenda-dia-vazio" />
                  ) : (
                    <button
                      key={dia}
                      type="button"
                      className={[
                        "agenda-dia",
                        dia === data ? "ativo" : "",
                        dia === hojeIso ? "hoje" : "",
                        dia < hojeIso ? "passado" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => {
                        setData(dia);
                        setErro(null);
                      }}
                    >
                      {Number(dia.slice(-2))}
                    </button>
                  ),
                )}
              </div>

              <div className="agenda-atalhos">
                <button type="button" className="btn-secondary btn-sm" onClick={() => setData(hojeIso)}>
                  Hoje
                </button>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => {
                    const d = new Date();
                    d.setDate(d.getDate() + 1);
                    setData(iso(d));
                    setMes(new Date(d.getFullYear(), d.getMonth(), 1));
                  }}
                >
                  Amanhã
                </button>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => {
                    const d = new Date();
                    d.setDate(d.getDate() + 7);
                    setData(iso(d));
                    setMes(new Date(d.getFullYear(), d.getMonth(), 1));
                  }}
                >
                  Em 7 dias
                </button>
              </div>
            </div>

            {/* ---------------- Horário ---------------- */}
            <div className="agenda-horarios">
              <span className="agenda-titulo">
                <IconClock size={15} /> Horário
              </span>
              <div className="agenda-horas">
                {HORARIOS.map((h) => (
                  <button
                    key={h}
                    type="button"
                    className={`agenda-hora ${h === horario ? "ativo" : ""}`}
                    onClick={() => setHorario(h)}
                  >
                    {h}
                  </button>
                ))}
              </div>
              <label className="field agenda-hora-livre">
                <span>Outro horário</span>
                <input type="time" value={horario} onChange={(e) => setHorario(e.target.value)} />
              </label>
            </div>
          </div>

          {/* ---------------- Dados da entrevista ---------------- */}
          <div className="doc-grid agenda-dados">
            <label className="field">
              <span>Etapa</span>
              <select value={etapa} onChange={(e) => setEtapa(e.target.value)}>
                {ETAPAS.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Entrevistador</span>
              <input
                value={entrevistador}
                onChange={(e) => setEntrevistador(e.target.value)}
                placeholder="Quem vai conduzir"
              />
            </label>
            <label className="field field-wide">
              <span>Observação (local, link da chamada, o que combinar)</span>
              <input
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                placeholder="Ex.: na sede, sala de reunião — levar documentos"
              />
            </label>
          </div>

          {data && (
            <p className={`agenda-resumo ${passado ? "agenda-resumo-alerta" : ""}`}>
              <IconCalendar size={15} /> {porExtenso(data)}
              {horario ? ` às ${horario}` : " (sem horário definido)"}
              {passado && " — atenção: essa data já passou."}
            </p>
          )}

          {erro && <div className="form-error">{erro}</div>}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onCancelar} disabled={salvando}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" onClick={confirmar} disabled={salvando}>
            {salvando ? "Marcando…" : "Marcar entrevista"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
